<template>
  <section class="fill-height">
    <v-toolbar color="#9190b5" dark prominent src="https://picsum.photos/1920/1080?random">
      <template v-slot:img="{ props }">
        <v-img v-bind="props" gradient="to top right, rgba(145,144,181,.7), rgba(25,32,72,.7)"></v-img>
      </template>
      <v-toolbar-title>{{ $route.name }}</v-toolbar-title>
      <v-spacer/>
      <template v-slot:extension>
        <v-tabs centered>
          <v-tab :to="{name: 'Session limits'}" class="text-no-wrap primary">Buy more sessions</v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <v-stepper v-if="session" v-model="stepper" vertical style="height: 100%">
      <v-stepper-step :complete="stepper > 1" :editable="stepper > 1 && stepper < 6" step="1">Practice area & Session type</v-stepper-step>
      <v-divider/>
      <v-stepper-content step="1" class="ma-0 pa-2 blur">
        <v-card>
          <v-card-text>
            <v-select v-model="session.practice_area_id"
                      :items="practice_areas"
                      label="Practice area"
                      item-text="name"
                      item-value="id"
                      :error-messages="api_errors['practice_area_id']"
                      solo
            />
            <v-select v-model="session.session_type_id"
                      :items="authUserSessionLimits.filter(item => item.status === 'independent')"
                      label="Session type"
                      item-text="name"
                      item-value="id"
                      :error-messages="api_errors['session_type_id']"
                      solo
            >
              <template v-slot:item="{item}">
                {{ item.name }}  <v-chip class="ma-2">{{ item.count }}</v-chip>
              </template>
            </v-select>
          </v-card-text>
          <v-divider/>
          <v-card-actions class="justify-end">
            <v-btn color="primary" class="box-shadow" :disabled="!session.practice_area_id || !session.session_type_id" @click="loadAvailableMocks">
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <v-stepper-step step="2" :complete="stepper > 2" :editable="stepper > 2 && stepper < 6" >Date and time</v-stepper-step>
      <v-divider/>
      <v-stepper-content step="2" class="ma-0 pa-2 blur">
        <v-alert type="error" dark dense icon="mdi-alert" prominent>
          <v-row align="center">
            <v-col class="grow">
              <p class="ma-0">Important notice: The system uses the UK time zone!</p>
              <p class="ma-0">Current UK time: {{ $moment().tz('Europe/London').format('DD MMMM YYYY hh:mm A')}}</p>
            </v-col>
          </v-row>
        </v-alert>

        <v-toolbar flat color="white" class="box-shadow">
          <v-btn small color="primary" class="box-shadow" @click="setToday">Today</v-btn>
          <v-spacer/>
          <v-btn small text @click="$refs.bookingCalendar.prev()">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-3">{{ $moment(focus).format('MMMM YYYY') }}</span>
          <v-btn small text @click="$refs.bookingCalendar.next()">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-spacer/>
          <v-toolbar-items style="height: auto">
            <v-select v-model="type"
                      :items="type_options"
                      item-text="label"
                      item-value="value"
                      menu-props="auto"
                      hide-details
                      single-line
                      outlined
                      dense
            ></v-select>
          </v-toolbar-items>
        </v-toolbar>

        <v-calendar ref="bookingCalendar"
                    :now="today"
                    :type="type"
                    v-model="focus"
                    color="primary"
                    :events="events"
                    :interval-minutes="90"
                    :interval-count="16"
                    :interval-height="40"
                    :event-more="false"
                    :event-color="getEventColor"
                    @click:event="selectSlot"
                    @change="loadAvailableSlots"
        ></v-calendar>
      </v-stepper-content>

      <v-stepper-step step="3" :complete="stepper > 3"
                      :editable="
                      session.session_type_id === 1 || session.session_type_id === 2
                      && stepper > 2
                      && stepper < 6"
                      :disabled="session.session_type_id !== 1 && session.session_type_id !== 2"
      >Tutor</v-stepper-step>
      <v-divider/>
      <v-stepper-content step="3" class="ma-0 pa-2 blur">
        <v-card>
          <v-card-text>
            <v-select label="Tutor"
                      v-model="session.tutor"
                      :items="available_tutors"
                      color="primary"
                      item-text="nickname"
                      item-value="id"
                      return-object
                      solo
            >
              <template v-slot:item="{item}">
                <v-list-item-action>{{ item.nickname }}</v-list-item-action>
                <v-list-item-content>
                  <div class="" v-html="item.summary"></div>
                </v-list-item-content>
              </template>
            </v-select>
          </v-card-text>
          <v-divider/>
          <v-card-actions class="justify-space-between">
            <v-btn text @click="stepper--">Back</v-btn>
            <v-btn color="primary" class="box-shadow" :disabled="!session.tutor || !session.mock" @click="toChildrenSession">
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <v-stepper-step step="4" :complete="stepper > 4" :editable="stepper > 2 && stepper < 6">Additional</v-stepper-step>
      <v-divider/>
      <v-stepper-content step="4" class="ma-0 pa-2 blur">
        <v-card v-if="children_session">
          <v-card-text v-if="session.mock && session.mock.children.length > 0">
            <v-subheader>More mocks are available. Want to book?</v-subheader>
            <v-select v-model="children_session.mock"
                      :items="session.mock.children"
                      item-text="full_name"
                      item-value="id"
                      hide-details
                      return-object
                      outlined
                      dense
            ></v-select>
          </v-card-text>

          <v-card-actions class="justify-end">
<!--            <v-btn text @click="stepper&#45;&#45;">Back</v-btn>-->
            <v-btn color="primary" class="box-shadow" :disabled="!children_session" @click="stepper++">Continue</v-btn>
          </v-card-actions>
        </v-card>
      </v-stepper-content>

      <v-stepper-step step="5">Confirmation</v-stepper-step>
      <v-divider/>
      <v-stepper-content step="5" class="ma-0 pa-2 blur">
        <v-card>
          <v-toolbar dense color="blur">Please double-check if the booking is correct</v-toolbar>
          <v-list-item three-line v-if="session && session.mock">
            <v-list-item-content>
              <v-list-item-title v-text="session.mock.full_name"/>
              <v-list-item-subtitle>{{ $moment(session.start).format('DD MMMM YYYY hh:mm A') }}</v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>

            </v-list-item-action>
          </v-list-item>
          <v-divider/>
          <!--            children_session-->
          <template v-if="children_session && children_session.mock">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-title v-text="children_session.mock.full_name"/>
                <v-list-item-subtitle>
                  {{ $moment(children_session.start).format('DD MMMM YYYY hh:mm A') }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>

              </v-list-item-action>
            </v-list-item>
            <v-divider/>
          </template>

          <v-card-text>
            <v-btn class="box-shadow my-3" color="primary" @click="reserveSessions" block>Confirm the booking</v-btn>
          </v-card-text>
        </v-card>
      </v-stepper-content>

      <v-stepper-step step="6">Complete</v-stepper-step>
      <v-divider/>
      <v-stepper-content step="6" class="ma-0 pa-2 blur">
        <v-card>
          <v-list-item three-line v-if="session">
            <v-list-item-content>
              <v-list-item-title v-text="session.name"/>
              <v-list-item-subtitle>
                {{ $moment(session.start).format('DD MMMM YYYY hh:mm A') }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn small class="box-shadow mt-3" color="success"
                     :to="{name: 'Classroom session', params: {session_id: session.id}}">
                open in classroom
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider/>
          <!--            children_session-->
          <template v-if="children_session">
            <v-list-item three-line>
              <v-list-item-content>
                <v-list-item-title v-text="children_session.name"/>
                <v-list-item-subtitle>
                  {{ $moment(children_session.start).format('DD MMMM YYYY hh:mm A') }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn small class="box-shadow mt-3" color="success"
                       :to="{name: 'Classroom session', params: {session_id: children_session.id}}">
                  open in classroom
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider/>
          </template>

          <v-card-text>
            <v-btn class="box-shadow my-3" color="primary" @click="stepper = 1" block>BOOK ANOTHER MOCK</v-btn>
            <v-btn class="box-shadow my-3" color="primary" :to="{name: 'Calendar'}" block>Go to calendar</v-btn>
            <v-btn class="box-shadow my-3" color="primary" :to="{name: 'Classroom'}" block>Go to classroom</v-btn>
          </v-card-text>
        </v-card>
      </v-stepper-content>
    </v-stepper>
  </section>
</template>

<script>
import {mapGetters} from 'vuex'

export default {
  data: () => ({
    stepper: 1,
    sessions_headers: [
      { text: 'Session type', value: 'name' },
      // { text: 'Type', value: 'status' },
      { text: 'Count', value: 'count' },
    ],

    today: '',
    focus: '',
    type: 'month',
    type_options: [
      { value: 'month', label: 'Month'},
      { value: 'week', label: 'Week'},
      { value: 'day', label: 'Day'},
    ],
    calendar_load_options: null,
    user_events: [],

    auto_search: true,
    auto_search_limit: 3,
    session: null,
    children_session: null,
    available_mocks: [],
    available_dates: [],
    available_tutors:[],
    available_tutor_mocks: [],

    api_errors: []
  }),
  computed: {
    ...mapGetters({
      practice_areas: 'getPracticeAreas',
      authUserStudyGroups: 'getAuthUserStudyGroups',
      authUserSessionLimits: 'getAuthUserSessionLimits',
    }),

    events: function () {
      let items = []
      if(this.authUserStudyGroups) {
        this.authUserStudyGroups.forEach(client_account => {
          if(client_account.study_group) {
            items.push({
              name: client_account.study_group.name,
              start: client_account.study_group.start_at,
              end: client_account.study_group.end_at,
              color: 'green',
              type: 'study_group'
            })
          }

          if(client_account.study_sub_group) {
            client_account.study_sub_group.p1_exam_dates.forEach(exam_date => {
              items.push({
                name: client_account.study_sub_group.short_name+' OSCE P1 exam',
                start: exam_date,
                end: exam_date,
                color: 'red',
                type: 'exam'
              })
            })
            client_account.study_sub_group.p2_exam_dates.forEach(exam_date => {
              items.push({
                name: client_account.study_sub_group.short_name+' OSCE P2 exam',
                start: exam_date,
                end: exam_date,
                color: 'red',
                type: 'exam'
              })
            })
          }

        })
      }

      // if(this.user_events.length > 0) {
      //   this.user_events.forEach(item => {
      //     items.push({...item, name: item.short_name, color: 'secondary'})
      //   })
      // }

      if (this.available_dates && this.available_dates.length > 0) {
        let mapped_items = this.available_dates
            .filter(date => {
              if(date.submission_deadline && this.$moment(date.submission_deadline).isBefore(this.$currentTime())) {
                return ;
              }
              if(date.date && date.date === this.$currentTime().format('YYYY-MM-DD')) return ;

              let exist = this.user_events.find(user_event => {
                let same_start =  this.$moment(`${date.date} ${date.time_start}`).isSame(user_event.start)
                let same_end =  this.$moment(`${date.date} ${date.time_end}`).isSame(user_event.end)
                if (same_start && same_end) return user_event;
              })

              if(!exist) return date;
            })
            .map(item => ({...item}))
        items.push(...mapped_items)
      }

      return items
    },
  },
  watch: {
    'session.tutor': function (val) {
      if(val !== null) {
        this.loadAvailableTutorMocks()
      }
    },
    'children_session.mock': function (val) {
      if(val !== null) {
        this.children_session.practice_area_id = val.practice_area_id
        this.children_session.session_type_id = val.session_type_id
        this.children_session.start = this.session.start
        this.children_session.end = this.session.end
      } else {
        this.children_session.practice_area_id = null
        this.children_session.session_type_id = null
        this.children_session.start = null
        this.children_session.end = null
      }
    },
    stepper: function (val) {
      if (val == 1) {
        this.api_errors = []
        this.type = 'month'
        this.initSession()
        this.initAutoSearch()
      }
      if (val == 2) {
        // this.tutor_id = null
        // this.mock = null
      }
    }
  },
  created() {
    this.setToday()
    this.initSession()
    this.initAutoSearch()
  },
  methods: {
    setToday() {
      this.today = this.$currentTime().format('YYYY-MM-DD')
      this.focus = this.$currentTime().format('YYYY-MM-DD')
      this.type = 'month'
    },
    getEventColor(event) {
      return event.color ? event.color : 'primary'
    },
    initSession() {
      this.session = {
        practice_area_id: null,
        session_type_id: null,
        tutor: null,
        mock: null,
        start: null,
        end: null,
      }
      this.children_session = null
    },
    initChildrenSession() {
      this.children_session = {
        practice_area_id: null,
        session_type_id: null,
        tutor: null,
        mock: null,
        start: null,
        end: null,
      }
    },
    initAutoSearch() {
      this.auto_search = true
      this.auto_search_limit = 3
    },

    loadAvailableMocks() {
      this.api_errors = []
      this.app_loading = true
      this.axios.get(`api/user/booking/available-mocks`, {
        params: {
          session_type_id: this.session.session_type_id,
          practice_area_id: this.session.practice_area_id,
        }
      })
          .then(({data}) => {
            this.available_mocks = data.data
            this.loadAvailableSlots(this.calendar_load_options)
            this.stepper++
          })
          .catch(errors => {this.api_errors = errors.response.data.errors})
          .then(() => {this.app_loading = false})
    },
    loadAvailableSlots({start, end}) {
      this.calendar_load_options = {start, end}
      if(!this.session || !this.session.session_type_id || !this.session.practice_area_id) return;
      this.app_loading = true

      this.axios.get('api/user/events', {params: {date_from: start.date, date_to: end.date}})
          .then(({data}) => {this.user_events = data.data})

      this.axios.get(`api/user/booking/available-slots`, {
        params: {
          session_type_id: this.session.session_type_id,
          practice_area_id: this.session.practice_area_id,
          mocks: this.available_mocks.map(item => item.id),
          date_from: start.date,
          date_to: end.date,
        }
      })
          .then(({data}) => {
            this.available_dates = data.data
            if(data.data.length === 0 && this.auto_search_limit > 0) {
              this.auto_search_limit--
              this.$refs.bookingCalendar.next()
            }
          })
          .catch(errors => { this.api_errors = errors.response.data.errors })
          .then(() => { this.app_loading = false })
    },
    selectSlot({event}) {
      if(event.type !== 'slot' && event.type !== 'cycle') return ;
      this.session.start = event.start
      this.session.end = event.end
      this.session.tutor = null
      this.available_tutors = []

      this.api_errors = []
      this.app_loading = true
      this.axios.get(`api/user/booking/available-tutors`, {
        params: {
          session_type_id: this.session.session_type_id,
          practice_area_id: this.session.practice_area_id,
          mocks: this.available_mocks.map(item => item.id),
          start: this.session.start,
          end: this.session.end,
        }
      })
          .then(({data}) => {
            this.available_tutors = data.data
            let tutor = this.available_tutors[0]
            this.session.tutor = tutor ? tutor : null
            if(this.session.session_type_id !== 1 && this.session.session_type_id !== 2) {
              this.loadAvailableTutorMocks().then(() => {
                this.stepper = 5
                // this.reserveSession()
              })
            }
            else {
              this.stepper++
            }
          })
          .catch(errors => {
            this.api_errors = errors.response.data.errors
            this.available_tutors = []
          })
          .then(() => { this.app_loading = false })
    },
    async loadAvailableTutorMocks() {
      this.api_errors = []
      this.app_loading = true
      this.available_tutor_mocks = []
      await this.axios.get(`api/user/booking/available-tutor-mocks`, {
        params: {
          session_type_id: this.session.session_type_id,
          practice_area_id: this.session.practice_area_id,
          tutor_id: this.session.tutor ? this.session.tutor.id :  null,
          mocks: this.available_mocks.map(item => item.id),
        }
      })
          .then(({data}) => {
            this.available_tutor_mocks = data.data
            this.session.mock = data.data[0]
          })
          .catch(errors => {this.api_errors = errors.response.data.errors})
          .then(() => {this.app_loading = false})
    },
    toChildrenSession() {
      if(this.session.mock.children && this.session.mock.children.length > 0) {
        this.initChildrenSession()
        this.session.mock.children.sort((a, b) => a.session_type_id < b.session_type_id)
        this.children_session.mock = this.session.mock.children[0]
        this.stepper++
      } else {
        this.stepper = this.stepper+2
      }
    },

    reserveSessions() {
      this.api_errors = []
      this.reserveSession().then(() => {
        if(this.children_session) {
          this.reserveChildrenSession()
        }
        this.stepper++
      })
    },
    async reserveSession() {
      this.api_errors = []
      this.app_loading = true
      await this.axios.post(`api/user/booking/reserve`, {
        session_type_id: this.session.session_type_id,
        practice_area_id: this.session.practice_area_id,
        mock_id: this.session.mock ? this.session.mock.id : null,
        tutor_id: this.session.tutor ? this.session.tutor.id : null,
        start: this.session.start,
        end: this.session.end,
      })
          .then(({data}) => {
            this.session = data.data
            this.$store.dispatch('loadAuthUserSessionLimits')
            this.$notify('Success!','success')
          })
          .catch(errors => {this.api_errors = errors.response.data.errors})
          .then(() => {this.app_loading = false})
    },
    reserveChildrenSession() {
      this.api_errors = []
      this.app_loading = true
      this.axios.post(`api/user/booking/reserve`, {
        parent_session_id: this.session.id,
        session_type_id: this.children_session.session_type_id,
        practice_area_id: this.children_session.practice_area_id,
        mock_id: this.children_session.mock ? this.children_session.mock.id : null,
        start: this.children_session.start,
        end: this.children_session.end,
      })
          .then(({data}) => {
            this.children_session = data.data
            this.$store.dispatch('loadAuthUserSessionLimits')
            this.$notify('Success!','success')
          })
          .catch(errors => {this.api_errors = errors.response.data.errors})
          .then(() => {this.app_loading = false})
    }
  }
}
</script>
